import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "ellen-a11y-prefs-v1";

// Patterns that would flag an actual Ghanaian or Ghanaian-language voice,
// if a browser/OS ever ships one. None do today (checked: Windows/Chrome
// only expose generic en-US voices), this exists so the feature upgrades
// itself automatically the day one becomes available, on any visitor's
// device, without a code change.
const GHANAIAN_VOICE_PATTERNS = [
  /^en-gh/i, /\bghana(ian)?\b/i, /\btwi\b/i, /\bakan\b/i, /\bga-gh/i, /\bdagbani\b/i, /\bewe\b/i,
];
// No exact match found: these are the next-closest English variants
// (West/Southern African or British English) some devices do ship.
const NEXT_CLOSEST_PATTERNS = [/^en-ng/i, /^en-za/i, /^en-gb/i];

function pickVoice(voices) {
  if (!voices.length) return { voice: null, isGhanaian: false };
  const ghanaian = voices.find((v) => GHANAIAN_VOICE_PATTERNS.some((p) => p.test(v.lang) || p.test(v.name)));
  if (ghanaian) return { voice: ghanaian, isGhanaian: true };
  const closest = voices.find((v) => NEXT_CLOSEST_PATTERNS.some((p) => p.test(v.lang)));
  if (closest) return { voice: closest, isGhanaian: false };
  return { voice: voices.find((v) => v.default) || voices[0], isGhanaian: false };
}

const DEFAULTS = {
  theme: "light", // "light" | "dark"
  cvd: "none", // "none" | "protanopia" | "deuteranopia" | "tritanopia"
  textScale: "base", // "base" | "lg" | "xl"
  readingSpace: false,
  highContrast: false,
};

function loadPrefs() {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (saved && typeof saved === "object") return { ...DEFAULTS, ...saved };
  } catch {
    // ignore malformed storage, fall through to defaults
  }
  // First-time visitors always land on the light theme, regardless of their
  // OS/browser color-scheme setting. They can still switch to dark from the
  // accessibility toolbar, which is what gets remembered from then on.
  return DEFAULTS;
}

const AccessibilityContext = createContext(null);

export function AccessibilityProvider({ children }) {
  const [prefs, setPrefs] = useState(loadPrefs);
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [voiceInfo, setVoiceInfo] = useState({ voice: null, isGhanaian: false });

  // Voices load async (and sometimes not until the first user gesture),
  // re-scan whenever the browser tells us the list changed.
  useEffect(() => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return undefined;
    const refresh = () => setVoiceInfo(pickVoice(window.speechSynthesis.getVoices()));
    refresh();
    window.speechSynthesis.addEventListener("voiceschanged", refresh);
    return () => window.speechSynthesis.removeEventListener("voiceschanged", refresh);
  }, []);

  // Apply every preference to <html> as classes/attributes so plain CSS
  // (see src/tailwind.css) does the actual repainting.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", prefs.theme === "dark");
    root.setAttribute("data-cvd", prefs.cvd);
    root.setAttribute("data-text-scale", prefs.textScale);
    root.setAttribute("data-reading-space", String(prefs.readingSpace));
    root.setAttribute("data-high-contrast", String(prefs.highContrast));
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      // storage can fail in private-browsing contexts, preferences just
      // won't persist across visits, which is a fine degradation.
    }
  }, [prefs]);

  const set = useCallback((patch) => setPrefs((p) => ({ ...p, ...patch })), []);
  const reset = useCallback(() => setPrefs(DEFAULTS), []);

  const stopSpeaking = useCallback(() => {
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
    setSpeaking(false);
    setPaused(false);
  }, []);

  const speak = useCallback(() => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const excluded = document.querySelectorAll("[data-a11y-exclude]");
    const clone = document.body.cloneNode(true);
    // Remove the corresponding nodes from the clone by matching data attr.
    clone.querySelectorAll("[data-a11y-exclude]").forEach((n) => n.remove());
    void excluded;
    const text = clone.innerText.replace(/\s+/g, " ").trim();
    if (!text) return;
    const utterance = new SpeechSynthesisUtterance(text.slice(0, 6000));
    utterance.rate = 1;
    if (voiceInfo.voice) utterance.voice = voiceInfo.voice;
    utterance.onstart = () => { setSpeaking(true); setPaused(false); };
    utterance.onend = () => { setSpeaking(false); setPaused(false); };
    utterance.onerror = () => { setSpeaking(false); setPaused(false); };
    window.speechSynthesis.speak(utterance);
  }, [voiceInfo]);

  const togglePause = useCallback(() => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    if (paused) {
      window.speechSynthesis.resume();
      setPaused(false);
    } else {
      window.speechSynthesis.pause();
      setPaused(true);
    }
  }, [paused]);

  const value = useMemo(
    () => ({ prefs, set, reset, speaking, paused, speak, togglePause, stopSpeaking, voiceInfo }),
    [prefs, set, reset, speaking, paused, speak, togglePause, stopSpeaking, voiceInfo]
  );

  return <AccessibilityContext.Provider value={value}>{children}</AccessibilityContext.Provider>;
}

export function useAccessibility() {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) throw new Error("useAccessibility must be used within AccessibilityProvider");
  return ctx;
}
